/**
 * ReplaySessionView — full-screen "真实案例回放" session page.
 *
 * Opened from a Playbook card's「看真实案例回放」action (which closes the playbook
 * overlay first, lifting the entry's full case LIST up to App). Instead of
 * cramming the recorded session into the playbook detail modal, this presents it
 * as a dedicated full-viewport overlay — the app has no router and the playbook
 * itself is an overlay over the home stage, so this stays consistent with that
 * pattern (mirrors the v6 full-screen session page design).
 *
 * Layout: an explanatory 说明 banner at the top (what this is, sentence case,
 * calm, with a 返回 control + Esc to close). When the entry binds MORE THAN ONE
 * case, a small case switcher (a row of selectable chips by `title`) renders
 * below the banner; selecting a chip loads that session's payload + narration
 * into the same `ReplayView`. The first case is the default. Below it the
 * existing chat `ReplayView` renders LARGE inside a `.rsv-stage` so the loom
 * session has full room and its internal conversation scroll reaches the last
 * message comfortably. The replay payload + optional narration are fetched here
 * exactly as the chat `ReplayBlock` does (GET /api/replay/:id [+ /narration]);
 * the replay renderer (play bar + reveal + narration audio) is reused verbatim —
 * only the container changes. LOOM-PRODUCT-COUPLED via the reused `ReplayView`.
 */
import { useEffect, useState, type JSX } from "react";
import type { ReplayNarration, ReplayPayload } from "@shared/types";
import type { PlaybookReplayCase } from "./types";
import { ReplayView } from "../components/chat/loom-replay/replay-view";

interface ReplaySessionViewProps {
  /** The bound worked-example cases (length ≥ 1); the first is the default. */
  replays: PlaybookReplayCase[];
  /** Zero-based index of the case to show first (defaults to 0 / first). */
  initialIndex?: number;
  /** Close the session page and return to where the user came from. */
  onClose: () => void;
  /** Whether loom-live (FEATURE_IA_LOOM_LIVE) is on — gates the「试跑」run-bar. */
  loomLiveEnabled?: boolean;
}

export function ReplaySessionView({
  replays,
  initialIndex = 0,
  onClose,
  loomLiveEnabled = false
}: ReplaySessionViewProps): JSX.Element | null {
  // Clamp the requested initial index into range so an out-of-bounds caller
  // value can never select a missing case.
  const startIndex = replays.length === 0 ? 0 : Math.min(Math.max(initialIndex, 0), replays.length - 1);
  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [payload, setPayload] = useState<ReplayPayload | null>(null);
  const [narration, setNarration] = useState<ReplayNarration | null>(null);
  const [error, setError] = useState<string | null>(null);

  const active = replays[activeIndex];
  const sessionId = active?.sessionId ?? "";
  const title = active?.title ?? "";

  // Fetch the active case's replay payload (required) + narration (optional; a
  // 404 simply means this session has none — silent reveal is used). Re-runs
  // whenever the selected case changes. Mirrors ReplayBlock.
  useEffect(() => {
    if (sessionId.length === 0) return undefined;
    let cancelled = false;
    setPayload(null);
    setNarration(null);
    setError(null);
    const sid = encodeURIComponent(sessionId);
    void (async (): Promise<void> => {
      try {
        const res = await fetch(`/api/replay/${sid}`);
        if (!res.ok) throw new Error(`replay fetch failed: ${res.status}`);
        const data = (await res.json()) as ReplayPayload;
        // Defensive: a malformed body (no messages array) would crash the
        // renderer; surface a graceful error state instead.
        if (!Array.isArray(data.messages)) {
          throw new Error("replay payload missing messages[]");
        }
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    void (async (): Promise<void> => {
      try {
        const res = await fetch(`/api/replay/${sid}/narration`);
        if (!res.ok) return;
        const data = (await res.json()) as ReplayNarration;
        if (!cancelled && Array.isArray(data.chapters) && data.chapters.length > 0) {
          setNarration(data);
        }
      } catch {
        // Ignore: narration is best-effort enrichment.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Esc closes the session page (mirrors the playbook overlay's Esc-to-close).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // No cases bound — nothing to show (defensive; callers gate on length ≥ 1).
  if (replays.length === 0) return null;

  const multi = replays.length > 1;

  return (
    <div className="rsv-overlay" role="dialog" aria-modal="true" aria-label="真实案例回放">
      <header className="rsv-banner">
        <div className="rsv-banner-text">
          <span className="rsv-banner-eyebrow">真实案例回放</span>
          <h2 className="rsv-banner-title">这是小环完成「{title}」的一次真实执行记录</h2>
          <p className="rsv-banner-sub">
            可暂停、逐步查看、重新播放；看完也可以自己问一遍，让小环为你的情况再走一遍。
          </p>
        </div>
        <button className="rsv-close" onClick={onClose} aria-label="返回">
          返回
        </button>
      </header>

      {/* Case switcher: only shown when more than one case is bound. Selecting a
          chip swaps the active case, re-fetching its payload + narration into the
          same ReplayView below. */}
      {multi ? (
        <div className="rsv-cases" role="tablist" aria-label="选择案例">
          {replays.map((c, idx) => (
            <button
              key={c.sessionId}
              type="button"
              role="tab"
              aria-selected={idx === activeIndex}
              className={`rsv-case-chip${idx === activeIndex ? " rsv-case-chip-active" : ""}`}
              onClick={() => setActiveIndex(idx)}
            >
              {c.title}
            </button>
          ))}
        </div>
      ) : null}

      <div className="rsv-stage">
        {/* Wrapped in `.replay-block-card` so the chat ReplayView styles (styles/
            loom-replay.css are scoped under that class) apply; `.rsv-stage`
            overrides the inline height cap so the session fills the page. */}
        <div className="replay-block-card">
          {error !== null ? (
            <div className="replay-card-state replay-card-error">
              回放加载失败，请稍后再试。
            </div>
          ) : payload === null ? (
            <div className="replay-card-state">正在加载会话回放…</div>
          ) : (
            <ReplayView
              key={sessionId}
              payload={payload}
              sessionId={sessionId}
              showToc
              loomLiveEnabled={loomLiveEnabled}
              {...(narration !== null ? { narration } : {})}
            />
          )}
        </div>
      </div>
    </div>
  );
}
